<script setup lang="ts">
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Textarea,
    ZhFieldError,
    ZhFormField,
    ZhFormLayout,
    ZhSavePreview,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { api, emptyCharter, type Member, parseConcurrentRunLimit } from '@/api'
import { useFieldValidation } from '@/composables/useFieldValidation'
import { useFormDraft } from '@/composables/useFormDraft'
import { useFormFunnel } from '@/composables/useFormFunnel'
import { docsUrl } from '@/lib/docs'
import { requiredName, SLUG_TAKEN } from '@/lib/fieldRules'
import { buildTeamPreview } from '@/lib/formPreview'
import { memberLabel } from '@/lib/memberLabel'
import { COO_ACTING_AS_LEAD, leadMemberIdOf } from '@/lib/teamLead'

/**
 * Every control here is the kit's (#1222): the fields are the kit `Input`, the lead the kit `Select`, the goal the
 * kit `Textarea`, on kit surfaces. The screen states no colour, radius, border or fill of its own.
 *
 * #1756: the form sits in the shared shell (title, one-sentence purpose, fields, actions bottom-right), every field
 * has one line of help, and Create team first shows a plain-words preview; nothing is sent until it is confirmed.
 *
 * A team is made with a goal, one sentence, and its KPIs are set on its page (#895). The seven-question charter and
 * the COO's draft of it are gone from here (#1844): the goal is the one line the team, the catalog and the public
 * site all show, and it is stored as the charter's purpose, which is where the product has always read the goal.
 */
const router = useRouter()
/** The form's funnel events (#1774), under the `team` flow. */
const funnel = useFormFunnel('team')
const name = ref('')
const slug = ref('')
/** The COO stands in until a member is chosen; the kit Select cannot hold an empty value for that. */
const leadChoice = ref<string>(COO_ACTING_AS_LEAD)
// The kit `Input` with `type="number"` hands up a `number` here at runtime once the field parses, despite
// the string initial value — see `parseConcurrentRunLimit`'s doc comment in `@/api`.
const concurrentRunLimit = ref<string | number>('')
const goal = ref('')
const members = ref<Member[]>([])
const error = ref<string | null>(null)
const saving = ref(false)
/**
 * Name is required (the field's own rule), checked while typing (#1757); a taken slug is shown on Slug. Concurrent
 * runs is blank or a whole number of 1 or more: `parseConcurrentRunLimit`'s own rule, which Create already refused.
 */
const fields = useFieldValidation(
    computed(() => ({ name: name.value, slug: slug.value, concurrentRunLimit: concurrentRunLimit.value })),
    {
        name: requiredName('team'),
        slug: () => null,
        concurrentRunLimit: value => {
            try {
                parseConcurrentRunLimit(value)
                return null
            } catch {
                return {
                    message: 'Concurrent runs is not a whole number of 1 or more.',
                    fix: 'Enter a whole number of at least 1, like 3, or leave it blank to use the enterprise limit.',
                }
            }
        },
    },
    // `create-team`, as this view's other ids are: `team-create` is a built-in task's slug (AGENTS.md rule 2).
    { idPrefix: 'create-team' },
)
/**
 * #1763: what is typed or picked here is kept in this browser until the team is created, so leaving (Cancel, another
 * link, a reload, a closed tab) loses nothing. Nothing of it is sent until Create team is confirmed.
 */
const draft = useFormDraft('create-team', { name, slug, leadChoice, concurrentRunLimit, goal })
// A restored draft is not a change made on this page: its fields show no error until changed or left.
fields.reset()

/** Where the engine detail behind Concurrent runs lives (one active run per member, the enterprise cap). */
const TEAMS_DOCS = docsUrl('/docs/company/teams')

/** The longest goal, once trimmed: one sentence, the line the catalog and the site show. */
const GOAL_MAX = 500

onMounted(async () => {
    try {
        members.value = await api.members()
        // A restored lead who is no longer a member: the COO stands in again, as it does before anyone is chosen.
        if (!members.value.some(member => member.id === leadChoice.value)) leadChoice.value = COO_ACTING_AS_LEAD
    } catch {
        members.value = []
    }
})

/** The save preview (#1756): open while its lines are held; any change to the form hides it as stale. */
const preview = ref<string[] | null>(null)

/**
 * A limit that is not a whole number is refused before the preview opens, with the message saving gave before: the
 * preview only ever describes a team that can be created. Validate first (CTO decision 19 on #1756): an invalid field
 * shows its error, takes focus and opens no preview.
 */
function openPreview(): void {
    if (fields.check() !== null) return
    error.value = null
    let concurrentRuns: number | null
    try {
        concurrentRuns = parseConcurrentRunLimit(concurrentRunLimit.value)
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not create (${caught.message}).` : 'Could not create.'
        return
    }
    const lead = members.value.find(member => member.id === leadMemberIdOf(leadChoice.value))
    preview.value = buildTeamPreview({
        name: name.value,
        leadName: lead ? memberLabel(lead) : null,
        concurrentRuns,
    })
}

// Any change hides a preview opened before it (#1756 CTO decision 15).
watch([name, slug, leadChoice, concurrentRunLimit, goal], () => {
    preview.value = null
})

async function confirmPreview(): Promise<void> {
    preview.value = null
    await create()
}

async function create(): Promise<void> {
    if (fields.check() !== null) return
    saving.value = true
    error.value = null
    try {
        const team = await api.createTeam({
            name: name.value,
            slug: slug.value || undefined,
            leadMemberId: leadMemberIdOf(leadChoice.value),
            charter: { ...emptyCharter(), purpose: goal.value.trim() },
            concurrentRunLimit: parseConcurrentRunLimit(concurrentRunLimit.value),
        })
        // Created: the next visit starts blank. A refused create keeps the draft, so what was typed is still here.
        draft.clear()
        // Saved: reported before the view leaves, so leaving for the new team is never counted as abandoning it.
        funnel.completed()
        await router.push(`/teams/${team.id}`)
    } catch (caught) {
        if (caught instanceof Error && caught.message === 'team_slug_taken') {
            fields.refuse('slug', SLUG_TAKEN)
            return
        }
        error.value = caught instanceof Error ? `Could not create (${caught.message}).` : 'Could not create.'
    } finally {
        saving.value = false
    }
}
</script>

<template>
	<section class="w-full space-y-6 p-8">
		<p class="text-muted-foreground text-sm">
			<RouterLink class="underline" to="/teams">Teams</RouterLink>
		</p>
		<ZhFormLayout
			title="Create team"
			purpose="Creates a team with a lead, a goal and the runs it may hold at once."
			data-testid="create-form"
			@submit.prevent="openPreview"
		>
			<template #status>
				<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
			</template>
			<div class="grid gap-3 sm:grid-cols-2">
				<!-- #1757's checks (CTO decisions 18 and 20 on #1756): a fixed id, described by the error, then the help line. -->
				<ZhFormField :id="fields.fieldId('name')" :error-id="fields.message('name').id" label="Name" help="What everyone will call the team." example="Sales">
					<template #default="{ controlId, describedBy }">
						<Input v-model="name" required v-bind="fields.bind('name')" :id="controlId" :aria-describedby="describedBy" />
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('name')" />
					</template>
				</ZhFormField>
				<ZhFormField :id="fields.fieldId('slug')" :error-id="fields.message('slug').id" label="Slug" help="The short name used in links, made from the name if empty." example="sales">
					<template #default="{ controlId, describedBy }">
						<Input v-model="slug" placeholder="from the name if empty" v-bind="fields.bind('slug')" :id="controlId" :aria-describedby="describedBy" />
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('slug')" />
					</template>
				</ZhFormField>
				<ZhFormField v-slot="{ controlId, helpId }" class="sm:col-span-2" label="Lead" label-id="create-team-lead" help="Who approves the team's work; the COO stands in until you choose." example="Ada Lovelace">
					<Select v-model="leadChoice">
						<SelectTrigger :id="controlId" class="w-full" aria-labelledby="create-team-lead" :aria-describedby="helpId">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem :value="COO_ACTING_AS_LEAD">COO acting as lead</SelectItem>
							<SelectItem v-for="member in members" :key="member.id" :value="member.id">{{ memberLabel(member) }}</SelectItem>
						</SelectContent>
					</Select>
				</ZhFormField>
				<ZhFormField
					:id="fields.fieldId('concurrentRunLimit')"
					:error-id="fields.message('concurrentRunLimit').id"
					class="sm:col-span-2"
					label="Concurrent runs"
					help="How many runs to hold for this team; blank shares the company's."
					example="3"
					:learn-more-href="TEAMS_DOCS"
				>
					<template #default="{ controlId, describedBy }">
						<Input
							v-model="concurrentRunLimit"
							data-testid="team-concurrency"
							type="number"
							min="1"
							placeholder="Inherits the enterprise limit when blank"
							v-bind="fields.bind('concurrentRunLimit')"
							:id="controlId"
							:aria-describedby="describedBy"
						/>
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('concurrentRunLimit')" />
					</template>
				</ZhFormField>
				<p class="text-muted-foreground text-xs sm:col-span-2">
					One active run per member still applies regardless of this limit.
				</p>
			</div>
			<ZhSectionHeading id="create-team-goal">Goal</ZhSectionHeading>
			<!-- One sentence: the line the team's page, the catalog and the public site show. KPIs are set on the team's page. -->
			<ZhFormField
				v-slot="{ controlId, helpId }"
				label="What is this team for, in one sentence?"
				:help="`Up to ${GOAL_MAX} characters. Set the team's KPIs on its page once it exists.`"
				example="We keep new customers happy"
			>
				<Textarea
					:id="controlId"
					v-model="goal"
					data-testid="team-goal"
					rows="2"
					:maxlength="GOAL_MAX"
					placeholder="For example: we keep customers happy after they sign up"
					:aria-describedby="helpId"
				/>
			</ZhFormField>
			<template #cancel>
				<Button type="button" size="sm" variant="outline" @click="router.push('/teams')">Cancel</Button>
			</template>
			<template #primary>
				<Button type="submit" size="sm" :disabled="saving">Create team</Button>
			</template>
			<template #preview>
				<ZhSavePreview
					v-if="preview"
					:lines="preview"
					confirm-label="Create team"
					:pending="saving"
					@confirm="confirmPreview"
					@edit="preview = null"
				/>
			</template>
		</ZhFormLayout>
	</section>
</template>
